import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Popconfirm,
  Row,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatString,
  handleChangePage,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";

const ExpenseType = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [filterConfig, setFilterConfig]: any = useState({ page: 1 });
  const [dataSize, setDataSize] = useState(0);
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [dataSource, setDataSource]: any = useState([]);
  const [errorMessage,         setErrorMessage]      = useState("");
  const [successMessage,       setSuccessMessage]    = useState("");

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
  ];

  async function submitForm(data: any) {
    setLoading("submit");
    var response = await postRequest(
      `expense_types/${showModal}`,
      { expense_type_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
        setSuccessMessage("");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} expense type.`
      );
    }
  }

  async function deleteExpenseType() {
    var response = await postRequest(
      `expense_types/delete`,
      {
        expense_type_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete expense type."
        )
      );
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    var response = await getRequest(`expense_types/get`, filterConfig, true);
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Expense Types"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Expense Type"
              onCreate={() => setShowModal("create")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={loading === "fetch"}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) =>
                      handleChangePage(page, setFilterConfig),
                    onShowSizeChange: (_: number, size: number) =>
                      handlePageSizeChange(size, setFilterConfig),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    var data = {
                      ...record,
                    };
                    setSelectedRow(data);
                    form.setFieldsValue({ ...data });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} Expense Type`}
        centered
        footer={null}
        onCancel={() => {
          setShowModal("");
          clearFields();
        }}
        width={700}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row className="w-full ps-1" gutter={[10, 10]}>
              <Col xs={24}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "This field is required.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col lg={24} className="flex justify-end gap-3">
                <Form.Item>
                  <Button
                    onClick={() => {
                      setShowModal("");
                      clearFields();
                    }}
                  >
                    Cancel
                  </Button>
                </Form.Item>
                {showModal === "update" && (
                  <Form.Item>
                    <Popconfirm
                      title="Are you sure you want to delete this expense type?"
                      onConfirm={() => deleteExpenseType()}
                      okText="Yes"
                    >
                      <Button danger>Delete</Button>
                    </Popconfirm>
                  </Form.Item>
                )}
                <Form.Item>
                  <Button
                    htmlType="submit"
                    type="primary"
                    loading={loading === "submit"}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
      <PersistentFooter />
      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />
    </Layout>
  );
};

export default ExpenseType;
